import React, { useState } from 'react';
import { useAuth } from './context/AuthContext';

// LoginGate has four screens, all rendered in the same card:
//   'login'   – username + password
//   'signup'  – email + username + password + confirm
//   'forgot'  – email-only form to request a one-time reset token
//   'reset'   – paste token + new password to actually change it
function LoginGate({ children }) {
  const {
    isAuthenticated,
    login,
    signup,
    requestPasswordReset,
    resetPassword,
    loading,
    error,
  } = useAuth();
  const [mode, setMode] = useState('login');

  // Shared fields
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [email, setEmail] = useState('');

  // Signup-only
  const [confirmPassword, setConfirmPassword] = useState('');

  // Reset-only
  const [resetToken, setResetToken] = useState('');

  const [localError, setLocalError] = useState('');
  const [notice, setNotice] = useState('');

  const clearMessages = () => {
    setLocalError('');
    setNotice('');
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    clearMessages();
    await login(username.trim(), password);
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    clearMessages();
    if (password !== confirmPassword) {
      setLocalError('Passwords do not match.');
      return;
    }
    if (password.length < 8) {
      setLocalError('Password must be at least 8 characters.');
      return;
    }
    const result = await signup({
      email: email.trim().toLowerCase(),
      username: username.trim(),
      password,
    });
    if (result && result.ok) {
      setMode('login');
      setPassword('');
      setConfirmPassword('');
      setEmail('');
      setNotice(
        'Account created. An administrator must approve it before you can log in.'
      );
    }
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    clearMessages();
    const result = await requestPasswordReset(email.trim().toLowerCase());
    if (result && result.ok) {
      setMode('reset');
      setPassword('');
      if (result.token) {
        // Backend now returns the raw one-time token directly (mirrors the
        // admin "issue token" flow). Pre-fill it so the user can just choose
        // a new password without copy/paste gymnastics.
        setResetToken(result.token);
        const expiresStr = result.expiresAt
          ? ` (expires ${result.expiresAt.replace('T', ' ').slice(0, 19)} UTC)`
          : '';
        setNotice(
          `One-time reset token generated${expiresStr}. ` +
          'Choose a new password below.'
        );
      } else {
        // Either the email is unknown or the account is disabled — keep the
        // response generic so we don't reveal which.
        setNotice(
          'If an account exists for that email, a one-time reset token has been ' +
          'generated. If you did not receive one, contact an administrator.'
        );
      }
    }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    clearMessages();
    if (password.length < 8) {
      setLocalError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setLocalError('Passwords do not match.');
      return;
    }
    const result = await resetPassword({ token: resetToken.trim(), password });
    if (result && result.ok) {
      setMode('login');
      setResetToken('');
      setPassword('');
      setConfirmPassword('');
      setNotice('Password updated. You can now log in with your new password.');
    }
  };

  const switchMode = (next) => {
    setMode(next);
    clearMessages();
    setPassword('');
    setConfirmPassword('');
    setResetToken('');
  };

  if (isAuthenticated) {
    return children;
  }

  const displayError = localError || error;

  // Per-mode form metadata.
  const titles = {
    login: 'Log in',
    signup: 'Create your account',
    forgot: 'Reset your password',
    reset: 'Choose a new password',
  };
  const submitLabels = {
    login: loading ? 'Signing in…' : 'Log In',
    signup: loading ? 'Creating account…' : 'Create account',
    forgot: loading ? 'Sending…' : 'Request reset token',
    reset: loading ? 'Updating…' : 'Set new password',
  };
  const handlers = {
    login: handleLogin,
    signup: handleSignup,
    forgot: handleForgot,
    reset: handleReset,
  };

  return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center">
      <div className="bg-white rounded-lg shadow-md p-8 w-full max-w-sm">
        <h2 className="text-2xl font-bold text-center text-blue-600 mb-2">Auction.ai</h2>
        <p className="text-center text-sm text-slate-500 mb-6">{titles[mode]}</p>

        <form onSubmit={handlers[mode]} className="space-y-4">
          {(mode === 'signup' || mode === 'forgot') && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full border border-slate-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                autoComplete="email"
                required
              />
            </div>
          )}

          {(mode === 'login' || mode === 'signup') && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Username</label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full border border-slate-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                autoFocus
                autoComplete="username"
                required
              />
            </div>
          )}

          {mode === 'reset' && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Reset token</label>
              <input
                type="text"
                value={resetToken}
                onChange={(e) => setResetToken(e.target.value)}
                className="w-full border border-slate-300 rounded-md px-3 py-2 font-mono text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                autoComplete="off"
                required
              />
            </div>
          )}

          {(mode === 'login' || mode === 'signup' || mode === 'reset') && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                {mode === 'reset' ? 'New password' : 'Password'}
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full border border-slate-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                required
              />
            </div>
          )}

          {(mode === 'signup' || mode === 'reset') && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Confirm password</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full border border-slate-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                autoComplete="new-password"
                required
              />
            </div>
          )}

          {notice && <p className="text-green-600 text-sm">{notice}</p>}
          {displayError && <p className="text-red-500 text-sm">{displayError}</p>}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-600 text-white py-2 rounded-md hover:bg-blue-700 transition disabled:opacity-50"
          >
            {submitLabels[mode]}
          </button>
        </form>

        <div className="mt-4 text-center text-sm text-slate-600 space-y-1">
          {mode === 'login' && (
            <>
              <div>
                Don&apos;t have an account?{' '}
                <button
                  type="button"
                  onClick={() => switchMode('signup')}
                  className="text-blue-600 hover:underline font-medium"
                >
                  Sign up
                </button>
              </div>
              <div>
                <button
                  type="button"
                  onClick={() => switchMode('forgot')}
                  className="text-blue-600 hover:underline font-medium"
                >
                  Forgot password?
                </button>
              </div>
            </>
          )}
          {mode === 'signup' && (
            <div>
              Already have an account?{' '}
              <button
                type="button"
                onClick={() => switchMode('login')}
                className="text-blue-600 hover:underline font-medium"
              >
                Log in
              </button>
            </div>
          )}
          {mode === 'forgot' && (
            <>
              <div>
                Already have a token?{' '}
                <button
                  type="button"
                  onClick={() => switchMode('reset')}
                  className="text-blue-600 hover:underline font-medium"
                >
                  Enter it now
                </button>
              </div>
              <div>
                <button
                  type="button"
                  onClick={() => switchMode('login')}
                  className="text-blue-600 hover:underline font-medium"
                >
                  Back to log in
                </button>
              </div>
            </>
          )}
          {mode === 'reset' && (
            <>
              <div>
                Need a new token?{' '}
                <button
                  type="button"
                  onClick={() => switchMode('forgot')}
                  className="text-blue-600 hover:underline font-medium"
                >
                  Request one
                </button>
              </div>
              <div>
                <button
                  type="button"
                  onClick={() => switchMode('login')}
                  className="text-blue-600 hover:underline font-medium"
                >
                  Back to log in
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default LoginGate;
